import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  Select,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  AlertDiv,
  CheckboxWrapper,
  SvgImage,
  ImageDiv,
  CopyImageButton,
  AdvancedOptionsButton,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo, FiHelpCircle } from 'react-icons/fi'
import { MdError } from 'react-icons/md'
import { FaRegCopy } from 'react-icons/fa6'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function ConcreteStairLongitudinalLanding() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [p, setP] = useState('')
  const [qtP, setQtP] = useState('')
  const [e, setE] = useState('')
  const [l, setL] = useState('')
  const [hp, setHp] = useState('')
  const [h, setH] = useState('')
  const [b, setB] = useState('')
  const [c, setC] = useState('')
  const [g2, setG2] = useState('')
  const [q, setQ] = useState('')
  const [rhoMin, setRhoMin] = useState(0.15)
  const [alphae, setAlphae] = useState(9.865)
  const [ecMultiply, setEcMultiply] = useState(5600)
  const [t0, setT0] = useState(7)
  const [forceAsOverwrite, setForceAsOverwrite] = useState(false)
  const [selectedRebar, setSelectedRebar] = useState('Ø5.0')
  const [selectedSecondaryRebar, setSelectedSecondaryRebar] = useState('Ø5.0')
  const [asOverwrite, setAsOverwrite] = useState(0)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)

  const HISTORY_KEY = 'concrete-stair-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setP(entry.p)
      setQtP(entry.qtP)
      setE(entry.e)
      setB(entry.b)
      setL(entry.l)
      setH(entry.h)
      setHp(entry.hp)
      setC(entry.c)
      setG2(entry.g2)
      setQ(entry.q)
      setRhoMin(entry.rhoMin)
      setAlphae(entry.alphae)
      setEcMultiply(entry.ecMultiply)
      setT0(entry.t0)
      setForceAsOverwrite(entry.forceAsOverwrite)
      setAsOverwrite(entry.asOverwrite)
      setResult(entry.result)
      setSelectedSecondaryRebar(entry.result?.secondaryAs?.options?.[0]?.name ?? 'Ø5.0')
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.p = validateField('p', p)
    newErrors.qtP = validateField('qtP', qtP)
    newErrors.e = validateField('e', e)
    newErrors.b = validateField('b', b)
    newErrors.l = validateField('l', l)
    newErrors.h = validateField('h', h)
    newErrors.hp = validateField('hp', hp)
    newErrors.c = validateField('c', c)
    newErrors.g2 = validateField('g2', g2)
    newErrors.q = validateField('q', q)
    newErrors.rhoMin = validateField('rhoMin', rhoMin)
    newErrors.alphae = validateField('alphae', alphae)
    newErrors.ecMultiply = validateField('ecMultiply', ecMultiply)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, p, qtP, e, b, l, h, hp, c, g2, q, rhoMin, alphae, ecMultiply, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fck,
        fyk,
        p,
        qtP,
        e,
        l,
        hp,
        b,
        h,
        c,
        g2,
        q,
        rhoMin,
        alphae,
        ecMultiply,
        t0,
        forceAsOverwrite,
        asOverwrite,
      }

      setData(data)

      const response = await api.post('/concrete/stair-longitudinal-landing', data)
      setResult(response.data)
      setSelectedSecondaryRebar(response.data.secondaryAs?.options?.[0]?.name ?? 'Ø5.0')

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        p,
        qtP,
        e,
        b,
        l,
        h,
        hp,
        c,
        g2,
        q,
        rhoMin,
        alphae,
        ecMultiply,
        t0,
        forceAsOverwrite,
        asOverwrite,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      p,
      qtP,
      e,
      b,
      l,
      h,
      hp,
      c,
      g2,
      q,
      rhoMin,
      alphae,
      ecMultiply,
      t0,
      forceAsOverwrite,
      asOverwrite,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Escada.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setP(jsonData.p)
        setQtP(jsonData.qtP)
        setE(jsonData.e)
        setB(jsonData.b)
        setL(jsonData.l)
        setH(jsonData.h)
        setHp(jsonData.hp)
        setC(jsonData.c)
        setG2(jsonData.g2)
        setQ(jsonData.q)
        setRhoMin(jsonData.rhoMin)
        setAlphae(jsonData.alphae)
        setEcMultiply(jsonData.ecMultiply)
        setT0(jsonData.t0)
        setForceAsOverwrite(jsonData.forceAsOverwrite)
        setAsOverwrite(jsonData.asOverwrite)
        setResult(jsonData.result)
        setSelectedSecondaryRebar(jsonData.result?.secondaryAs?.options?.[0]?.name ?? 'Ø5.0')
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const modelImageRef = useRef(null)
  const rebarsImageRef = useRef(null)

  const ModelImage = () => {
    return (
      <SvgImage ref={modelImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <line x1="65.5958" y1="345.31" x2="292.048" y2="51.3897" stroke="black" strokeWidth="2" />
        <line x1="293.508" y1="51" x2="441.804" y2="51" stroke="black" strokeWidth="2" />
        <path d="M422.298 82.394H461.109L441.704 52L422.298 82.394Z" stroke="black" strokeWidth="2" />
        <path d="M420.428 89.408H461.577" stroke="black" strokeWidth="2" />
        <path d="M45.2776 377.308H85.3576L65.3176 345.92L45.2776 377.308Z" stroke="black" strokeWidth="2" />
        <path d="M44.7947 377.791L39 386" stroke="black" strokeWidth="2" />
        <path d="M52.0381 377.791L46.2434 386" stroke="black" strokeWidth="2" />
        <path d="M59.2815 377.791L53.4868 386" stroke="black" strokeWidth="2" />
        <path d="M66.5248 377.791L60.7301 386" stroke="black" strokeWidth="2" />
        <path d="M73.7682 377.791L67.9735 386" stroke="black" strokeWidth="2" />
        <path d="M81.0116 377.791L75.2169 386" stroke="black" strokeWidth="2" />
        <path d="M86.3234 377.791L80.5287 386" stroke="black" strokeWidth="2" />
        <text
          transform="translate(153 418.618)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="20.3466" y="13.1604">
            {p !== '' && qtP !== '' ? p * qtP : 'l1'}
          </tspan>
        </text>
        <text
          transform="translate(342 418.618)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="23.5498" y="13.1604">
            {l !== '' && l != 0 ? l : 'l'}
          </tspan>
        </text>
        <path
          d="M293 437L288 434.113V439.887L293 437ZM65 437L70 439.887V434.113L65 437ZM288.5 437V436.5H69.5V437V437.5H288.5V437Z"
          fill="black"
        />
        <path
          d="M442 437L437 434.113V439.887L442 437ZM294 437L299 439.887V434.113L294 437ZM437.5 437V436.5H298.5V437V437.5H437.5V437Z"
          fill="black"
        />
        <line x1="65" y1="390" x2="65" y2="448.41" stroke="black" />
        <line x1="293.5" y1="67" x2="293.5" y2="448" stroke="black" />
        <line x1="441.5" y1="97" x2="441.5" y2="448" stroke="black" />
      </SvgImage>
    )
  }

  const RebarImage = () => {
    return (
      <SvgImage ref={rebarsImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <path d="M57.4973 266.098H33.27" stroke="black" strokeWidth="2" />
        <path d="M33.27 266.098V335.268" stroke="black" strokeWidth="2" />
        <path d="M33.27 335.269H57.4973" stroke="black" strokeWidth="2" />
        <path d="M412.878 140.841H214.004" stroke="black" strokeWidth="2" />
        <path d="M214.004 140.841V172.161" stroke="black" strokeWidth="2" />
        <path d="M214.004 202.451L273.085 166.777" stroke="black" strokeWidth="2" />
        <path d="M273.085 166.777H412.878" stroke="black" strokeWidth="2" />
        <path d="M412.879 166.777V140.841" stroke="black" strokeWidth="2" />
        <path d="M110.236 234.778H57.4973" stroke="black" strokeWidth="2" />
        <path d="M57.4973 234.778V260.714" stroke="black" strokeWidth="2" />
        <path d="M117.445 260.714L214.004 202.451" stroke="black" strokeWidth="2" />
        <path d="M214.004 172.161H162.108" stroke="black" strokeWidth="2" />
        <path d="M162.108 172.161V203.458" stroke="black" strokeWidth="2" />
        <path d="M162.108 203.458H110.236" stroke="black" strokeWidth="2" />
        <path d="M110.236 203.458V234.778" stroke="black" strokeWidth="2" />
        <path d="M57.4973 296.902L117.445 260.714" stroke="black" strokeWidth="2" />
        <path d="M57.4973 296.903V335.268" stroke="black" strokeWidth="2" />
        <path d="M57.4973 266.098V257.249" stroke="black" strokeWidth="2" />
        <path d="M57.4973 266.098H33.27" stroke="black" strokeWidth="2" />
        <path d="M33.27 266.098V335.268" stroke="black" strokeWidth="2" />
        <path d="M33.27 335.269H57.4973" stroke="black" strokeWidth="2" />
        <path d="M57.4973 296.903L214.004 202.451" stroke="black" strokeWidth="2" />
        <path d="M78 317L270 201" stroke="black" />
        <path d="M81.2321 317.999L60 290.588" stroke="black" />
        <path d="M214.004 202.451L273.085 166.777" stroke="black" strokeWidth="2" />
        <path d="M273.085 166.777H412.878" stroke="black" strokeWidth="2" />
        <path d="M412.879 166.777V140.841" stroke="black" strokeWidth="2" />
        <path d="M412.878 140.841H214.004" stroke="black" strokeWidth="2" />
        <path d="M214.004 140.841V172.161" stroke="black" strokeWidth="2" />
        <path d="M214.004 172.161H162.108" stroke="black" strokeWidth="2" />
        <path d="M162.108 172.161V203.458" stroke="black" strokeWidth="2" />
        <path d="M162.108 203.458H110.236" stroke="black" strokeWidth="2" />
        <path d="M110.236 203.458V234.778" stroke="black" strokeWidth="2" />
        <path d="M110.236 234.778H57.4973" stroke="black" strokeWidth="2" />
        <path d="M57.4973 234.778V260.714" stroke="black" strokeWidth="2" />
        <path
          d="M58.6912 289.904C58.6912 290.105 58.7713 290.299 58.914 290.442C59.0566 290.584 59.2502 290.664 59.452 290.664C59.6537 290.664 59.8472 290.584 59.9899 290.442C60.1325 290.299 60.2127 290.105 60.2127 289.904"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M60.2127 289.904C60.2127 289.702 60.1325 289.508 59.9899 289.366C59.8472 289.223 59.6537 289.143 59.452 289.143C59.2502 289.143 59.0566 289.223 58.914 289.366C58.7713 289.508 58.6912 289.702 58.6912 289.904"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M80.9288 276.491C80.9288 276.692 81.009 276.886 81.1516 277.029C81.2943 277.171 81.4879 277.251 81.6896 277.251C81.8914 277.251 82.0849 277.171 82.2275 277.029C82.3702 276.886 82.4503 276.692 82.4503 276.491"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M82.4503 276.491C82.4503 276.289 82.3702 276.096 82.2275 275.953C82.0849 275.81 81.8914 275.73 81.6896 275.73C81.4879 275.73 81.2943 275.81 81.1516 275.953C81.009 276.096 80.9288 276.289 80.9288 276.491"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M103.143 263.078C103.143 263.28 103.223 263.473 103.366 263.616C103.509 263.759 103.702 263.839 103.904 263.839C104.106 263.839 104.299 263.759 104.442 263.616C104.584 263.473 104.665 263.28 104.665 263.078"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M104.665 263.078C104.665 262.876 104.584 262.683 104.442 262.54C104.299 262.397 104.106 262.317 103.904 262.317C103.702 262.317 103.509 262.397 103.366 262.54C103.223 262.683 103.143 262.876 103.143 263.078"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M125.358 249.642C125.358 249.843 125.438 250.037 125.58 250.18C125.723 250.322 125.916 250.402 126.118 250.402C126.32 250.402 126.514 250.322 126.656 250.18C126.799 250.037 126.879 249.843 126.879 249.642"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M126.879 249.642C126.879 249.44 126.799 249.247 126.656 249.104C126.514 248.961 126.32 248.881 126.118 248.881C125.916 248.881 125.723 248.961 125.58 249.104C125.438 249.247 125.358 249.44 125.358 249.642"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M147.572 236.229C147.572 236.431 147.652 236.624 147.795 236.767C147.937 236.91 148.131 236.99 148.333 236.99C148.534 236.99 148.728 236.91 148.87 236.767C149.013 236.624 149.093 236.431 149.093 236.229"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M149.093 236.229C149.093 236.027 149.013 235.834 148.87 235.691C148.728 235.548 148.534 235.468 148.333 235.468C148.131 235.468 147.937 235.548 147.795 235.691C147.652 235.834 147.572 236.027 147.572 236.229"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M169.786 222.816C169.786 223.018 169.866 223.211 170.009 223.354C170.152 223.497 170.345 223.577 170.547 223.577C170.749 223.577 170.942 223.497 171.085 223.354C171.227 223.211 171.308 223.018 171.308 222.816"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M171.308 222.816C171.308 222.614 171.227 222.421 171.085 222.278C170.942 222.136 170.749 222.055 170.547 222.055C170.345 222.055 170.152 222.136 170.009 222.278C169.866 222.421 169.786 222.614 169.786 222.816"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M192 209.403C192 209.605 192.08 209.799 192.223 209.941C192.366 210.084 192.559 210.164 192.761 210.164C192.963 210.164 193.156 210.084 193.299 209.941C193.442 209.799 193.522 209.605 193.522 209.403"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M193.522 209.403C193.522 209.201 193.442 209.008 193.299 208.865C193.156 208.723 192.963 208.642 192.761 208.642C192.559 208.642 192.366 208.723 192.223 208.865C192.08 209.008 192 209.201 192 209.403"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M214.214 195.991C214.214 196.192 214.294 196.386 214.437 196.529C214.58 196.671 214.773 196.751 214.975 196.751C215.177 196.751 215.37 196.671 215.513 196.529C215.656 196.386 215.736 196.192 215.736 195.991"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M215.736 195.99C215.736 195.789 215.656 195.595 215.513 195.452C215.37 195.31 215.177 195.23 214.975 195.23C214.773 195.23 214.58 195.31 214.437 195.452C214.294 195.595 214.214 195.789 214.214 195.99"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M236.429 182.554C236.429 182.756 236.509 182.95 236.651 183.092C236.794 183.235 236.988 183.315 237.189 183.315C237.391 183.315 237.585 183.235 237.727 183.092C237.87 182.95 237.95 182.756 237.95 182.554"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M237.95 182.554C237.95 182.352 237.87 182.159 237.727 182.016C237.585 181.874 237.391 181.793 237.189 181.793C236.988 181.793 236.794 181.874 236.651 182.016C236.509 182.159 236.429 182.352 236.429 182.554"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M258.666 169.141C258.666 169.343 258.746 169.537 258.889 169.679C259.032 169.822 259.225 169.902 259.427 169.902C259.629 169.902 259.822 169.822 259.965 169.679C260.108 169.537 260.188 169.343 260.188 169.141"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M260.188 169.141C260.188 168.94 260.108 168.746 259.965 168.603C259.822 168.461 259.629 168.381 259.427 168.381C259.225 168.381 259.032 168.461 258.889 168.603C258.746 168.746 258.666 168.94 258.666 169.141"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M406.839 161.698C406.839 161.9 406.919 162.093 407.062 162.236C407.205 162.378 407.398 162.459 407.6 162.459C407.802 162.459 407.995 162.378 408.138 162.236C408.281 162.093 408.361 161.9 408.361 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M408.361 161.698C408.361 161.496 408.281 161.302 408.138 161.16C407.995 161.017 407.802 160.937 407.6 160.937C407.398 160.937 407.205 161.017 407.062 161.16C406.919 161.302 406.839 161.496 406.839 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M389.541 161.698C389.541 161.9 389.621 162.093 389.763 162.236C389.906 162.378 390.1 162.459 390.301 162.459C390.503 162.459 390.697 162.378 390.839 162.236C390.982 162.093 391.062 161.9 391.062 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M391.062 161.698C391.062 161.496 390.982 161.302 390.839 161.16C390.697 161.017 390.503 160.937 390.301 160.937C390.1 160.937 389.906 161.017 389.763 161.16C389.621 161.302 389.541 161.496 389.541 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M372.266 161.698C372.266 161.9 372.346 162.093 372.488 162.236C372.631 162.378 372.825 162.459 373.026 162.459C373.228 162.459 373.422 162.378 373.564 162.236C373.707 162.093 373.787 161.9 373.787 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M373.787 161.698C373.787 161.496 373.707 161.302 373.564 161.16C373.422 161.017 373.228 160.937 373.026 160.937C372.825 160.937 372.631 161.017 372.488 161.16C372.346 161.302 372.266 161.496 372.266 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M354.967 161.698C354.967 161.9 355.047 162.093 355.19 162.236C355.333 162.378 355.526 162.459 355.728 162.459C355.93 162.459 356.123 162.378 356.266 162.236C356.408 162.093 356.489 161.9 356.489 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M356.489 161.698C356.489 161.496 356.408 161.302 356.266 161.16C356.123 161.017 355.93 160.937 355.728 160.937C355.526 160.937 355.333 161.017 355.19 161.16C355.047 161.302 354.967 161.496 354.967 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M337.668 161.698C337.668 161.9 337.749 162.093 337.891 162.236C338.034 162.378 338.227 162.459 338.429 162.459C338.631 162.459 338.824 162.378 338.967 162.236C339.11 162.093 339.19 161.9 339.19 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M339.19 161.698C339.19 161.496 339.11 161.302 338.967 161.16C338.824 161.017 338.631 160.937 338.429 160.937C338.227 160.937 338.034 161.017 337.891 161.16C337.749 161.302 337.668 161.496 337.668 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M320.37 161.698C320.37 161.9 320.45 162.093 320.593 162.236C320.735 162.378 320.929 162.459 321.131 162.459C321.332 162.459 321.526 162.378 321.669 162.236C321.811 162.093 321.891 161.9 321.891 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M321.891 161.698C321.891 161.496 321.811 161.302 321.669 161.16C321.526 161.017 321.332 160.937 321.131 160.937C320.929 160.937 320.735 161.017 320.593 161.16C320.45 161.302 320.37 161.496 320.37 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M303.095 161.698C303.095 161.9 303.175 162.093 303.318 162.236C303.46 162.378 303.654 162.459 303.856 162.459C304.057 162.459 304.251 162.378 304.393 162.236C304.536 162.093 304.616 161.9 304.616 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M304.616 161.698C304.616 161.496 304.536 161.302 304.393 161.16C304.251 161.017 304.057 160.937 303.856 160.937C303.654 160.937 303.46 161.017 303.318 161.16C303.175 161.302 303.095 161.496 303.095 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M285.796 161.698C285.796 161.9 285.876 162.093 286.019 162.236C286.162 162.378 286.355 162.459 286.557 162.459C286.759 162.459 286.952 162.378 287.095 162.236C287.238 162.093 287.318 161.9 287.318 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M287.318 161.698C287.318 161.496 287.238 161.302 287.095 161.16C286.952 161.017 286.759 160.937 286.557 160.937C286.355 160.937 286.162 161.017 286.019 161.16C285.876 161.302 285.796 161.496 285.796 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M268.498 161.698C268.498 161.9 268.578 162.093 268.72 162.236C268.863 162.378 269.057 162.459 269.258 162.459C269.46 162.459 269.654 162.378 269.796 162.236C269.939 162.093 270.019 161.9 270.019 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M270.019 161.698C270.019 161.496 269.939 161.302 269.796 161.16C269.654 161.017 269.46 160.937 269.258 160.937C269.057 160.937 268.863 161.017 268.72 161.16C268.578 161.302 268.498 161.496 268.498 161.698"
          stroke="black"
          strokeWidth="2"
        />
        <path d="M409.414 144.305V163.336" stroke="black" strokeWidth="2" />
        <path d="M409.414 163.336H235.328" stroke="black" strokeWidth="2" />
        <path d="M235.328 163.336L163.911 206.43" stroke="black" strokeWidth="2" />
        <path d="M163.911 206.43L173.742 222.722" stroke="black" strokeWidth="2" />
        <path d="M453 29.4423V48.473" stroke="black" strokeWidth="2" />
        <path d="M453 48.473H278.914" stroke="black" strokeWidth="2" />
        <path d="M278.914 48.473L207.496 91.5673" stroke="black" strokeWidth="2" />
        <path d="M207.496 91.567L217.328 107.859" stroke="black" strokeWidth="2" />
        <path d="M57.4973 296.903V335.268" stroke="black" strokeWidth="2" />
        <path d="M57.4973 266.098V257.249" stroke="black" strokeWidth="2" />
        <path d="M409.414 163.336V144.305" stroke="black" strokeWidth="2" />
        <path d="M409.414 144.305H303.656" stroke="black" strokeWidth="2" />
        <path d="M303.656 144.305L36.7344 305.4" stroke="black" strokeWidth="2" />
        <path d="M36.7344 305.4V331.804" stroke="black" strokeWidth="2" />
        <path d="M409.414 308.63V289.599" stroke="black" strokeWidth="2" />
        <path d="M409.414 289.599H303.656" stroke="black" strokeWidth="2" />
        <path d="M303.656 289.599L36.7344 450.694" stroke="black" strokeWidth="2" />
        <path d="M36.7344 450.693V477.098" stroke="black" strokeWidth="2" />
        <text
          transform="translate(131.5 366.078) rotate(-30.0855)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="10.8251" y="13.1604">
            {result &&
              (Number(result.bending1.as) > Number(result.bending2.as)
                ? result.bending1.asRebars
                : result.bending2.asRebars
              ).find((r) => r.name == selectedRebar)?.quantity}{' '}
            {selectedRebar}
          </tspan>
        </text>
        <text
          transform="translate(330 29)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="10.8251" y="13.1604">
            {result &&
              (Number(result.bending1.as) > Number(result.bending2.as)
                ? result.bending1.asRebars
                : result.bending2.asRebars
              ).find((r) => r.name == selectedRebar)?.quantity}{' '}
            {selectedRebar}
          </tspan>
        </text>
        <text
          transform="translate(325 183.5)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="8.27632" y="13.1604">
            {selectedSecondaryRebar} c/
            {result.secondaryAs.options.find((r) => r.name == selectedSecondaryRebar)?.spacing}
          </tspan>
        </text>
        <text
          transform="translate(175.165 270.54) rotate(-31.1889)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="8.27632" y="13.1604">
            {selectedSecondaryRebar} c/
            {result.secondaryAs.options.find((r) => r.name == selectedSecondaryRebar)?.spacing}
          </tspan>
        </text>
        <line x1="269.5" y1="172" x2="269.5" y2="205" stroke="black" />
        <line x1="407.5" y1="174" x2="407.5" y2="205" stroke="black" />
        <line x1="266" y1="201.5" x2="412" y2="201.5" stroke="black" />
      </SvgImage>
    )
  }

  const handleCopySVG = async (svgRef) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 500
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Escada</span>
          </div>
          <h1>📉 Escada em Concreto Armado</h1>
          <p className="subtitle">Dimensionamento de escadas de concreto armado patamar intermediário</p>
          <div className="badges">
            <Badge>⭐ Popular</Badge>
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>⚡ Patamar</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento completo de escadas de concreto armado com patamar
              intermediário, conforme a NBR 6118:2014.
            </p>
            <p>
              O cálculo considera os carregamentos permanentes e acidentais, determina os esforços nos lances e patamar,
              e fornece a armadura necessária com sugestões de bitolas adequadas para o dimensionamento.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Tabelas de Armadura</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <ImageDiv>
              <ModelImage />
              <CopyImageButton
                type="button"
                title="Clique para copiar essa imagem"
                onClick={() => handleCopySVG(modelImageRef)}
              >
                <FaRegCopy />
                Copiar
              </CopyImageButton>
            </ImageDiv>
            <InputDiv>
              <span>Piso (cm)</span>
              <ValidatedInput
                name="p"
                type="number"
                value={p || ''}
                onChange={(event) => setP(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Quantidade de pisos</span>
              <ValidatedInput
                name="qtP"
                type="number"
                value={qtP || ''}
                onChange={(event) => setQtP(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espelho (cm)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e || ''}
                onChange={(event) => setE(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Largura do lance (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Comprimento do patamar (cm)</span>
              <ValidatedInput
                name="l"
                type="number"
                value={l || ''}
                onChange={(event) => setL(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura no lance (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura no patamar (cm)</span>
              <ValidatedInput
                name="hp"
                type="number"
                value={hp || ''}
                onChange={(event) => setHp(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="c"
                type="number"
                value={c || ''}
                onChange={(event) => setC(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Carregamento permanente (g2, kN/m²)</span>
              <ValidatedInput
                name="g2"
                type="number"
                value={g2 || ''}
                onChange={(event) => setG2(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Carregamento acidental (q, kN/m²)</span>
              <ValidatedInput
                name="q"
                type="number"
                value={q || ''}
                onChange={(event) => setQ(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Taxa de aço mínima (%)</span>
              <ValidatedInput
                name="rhoMin"
                type="number"
                value={rhoMin ?? ''}
                onChange={(event) => setRhoMin(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <AdvancedOptionsButton
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              $isOpen={showAdvancedOptions}
            >
              <span className="arrow-icon">{showAdvancedOptions ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvancedOptions ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>
            {showAdvancedOptions && (
              <>
                <InputDiv>
                  <span>
                    Relação dos módulos de elasticidade (&alpha;<sub>e</sub>)
                    <FiHelpCircle
                      aria-label="Ajuda sobre relacao dos modulos de elasticidade"
                      data-tooltip-id="stair-tooltip"
                      data-tooltip-content="Relação entre os modulos de elasticidade do aco e do concreto (Es/Ec)."
                      data-tooltip-place="top"
                      style={{ marginLeft: '6px', verticalAlign: 'middle', cursor: 'help' }}
                    />
                  </span>
                  <ValidatedInput
                    name="alphae"
                    type="number"
                    value={alphae || ''}
                    onChange={(event) => setAlphae(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>
                    Multiplicador para obter o módulo do concreto
                    <FiHelpCircle
                      aria-label="Ajuda sobre multiplicador para módulo de elasticidade"
                      data-tooltip-id="stair-tooltip"
                      data-tooltip-content="Valor que multiplicado por raiz do fck obtem o módulo de elasticidade do concreto (Ec)."
                      data-tooltip-place="top"
                      style={{ marginLeft: '6px', verticalAlign: 'middle', cursor: 'help' }}
                    />
                  </span>
                  <ValidatedInput
                    name="ecMultiply"
                    type="number"
                    value={ecMultiply || ''}
                    onChange={(event) => setEcMultiply(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Idade do concreto na aplicação da carga (dias)</span>
                  <ValidatedInput
                    name="t0"
                    type="number"
                    value={t0 || ''}
                    onChange={(event) => setT0(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <div style={{ marginTop: '15px', marginBottom: '10px' }}>
                  <CheckboxWrapper>
                    <input
                      type="checkbox"
                      checked={forceAsOverwrite}
                      onChange={(e) => setForceAsOverwrite(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span>
                      Forçar área de aço
                      <FiHelpCircle
                        aria-label="Forçar área de aço"
                        data-tooltip-id="stair-tooltip"
                        data-tooltip-content="Essa área de aço será utilizada para calcular a inércia equivalente por Branson."
                        data-tooltip-place="top"
                        style={{ marginLeft: '6px', verticalAlign: 'middle', cursor: 'help' }}
                      />
                    </span>
                  </CheckboxWrapper>
                </div>
                {forceAsOverwrite && (
                  <InputDiv>
                    <span>Área de aço (cm²)</span>
                    <ValidatedInput
                      name="asOverwrite"
                      type="number"
                      value={asOverwrite || ''}
                      onChange={(event) => setAsOverwrite(Number(event.target.value))}
                      errors={errors}
                      touched={touched}
                      onBlur={handleBlur}
                      disabled={isLoading}
                    />
                  </InputDiv>
                )}
              </>
            )}
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Esforços</h3>
              <MathDiv>
                <h4>Lance</h4>
                <BlockMath math={`M_d = ${result.md1.toFixed(0)} \\text{ kN} \\cdot \\text{cm}`} />
                <h4>Patamar</h4>
                <BlockMath math={`M_d = ${result.md2.toFixed(0)} \\text{ kN} \\cdot \\text{cm}`} />
              </MathDiv>
              {result.bending1.xdratio != 0.45 && result.bending2.xdratio != 0.45 ? (
                <>
                  <h3>Cálculo da armação</h3>
                  <MathDiv>
                    <h4>Armação principal</h4>
                    <BlockMath math={`A_s= ${result.as.toFixed(2)} \\text{ cm}^2`} />
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="rebarSelect"
                        value={selectedRebar}
                        onChange={(event) => setSelectedRebar(event.target.value)}
                      >
                        {(Number(result.bending1.as) > Number(result.bending2.as)
                          ? result.bending1.asRebars
                          : result.bending2.asRebars
                        ).map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Serão necessários{' '}
                      {
                        (Number(result.bending1.as) > Number(result.bending2.as)
                          ? result.bending1.asRebars
                          : result.bending2.asRebars
                        ).find((r) => r.name == selectedRebar).quantity
                      }{' '}
                      barras de {selectedRebar}
                    </p>
                    <h4>Armação secundária</h4>
                    <BlockMath
                      math={`A_{s,distribuicao}= ${result.secondaryAs.value.toFixed(2)} \\text{ cm}^2\\text{/m}`}
                    />
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="secondaryRebarSelect"
                        value={selectedSecondaryRebar}
                        onChange={(event) => setSelectedSecondaryRebar(event.target.value)}
                      >
                        {result.secondaryAs.options.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário {selectedSecondaryRebar} c/
                      {result.secondaryAs.options.find((r) => r.name == selectedSecondaryRebar)?.spacing}
                    </p>
                  </MathDiv>
                  <h3>Verificação da deformação</h3>
                  <MathDiv>
                    <h4>Flecha diferida</h4>
                    <BlockMath
                      math={`\\delta_{t,\\infty} = \\delta_{t,0} \\cdot ${(1 + result.alphaf).toFixed(2)} = ${(result.deltaf * 100).toFixed(2)} \\text{ cm}`}
                    />
                    <h4>Flecha limite</h4>
                    <BlockMath math={`\\delta_{lim} = ${(result.dlim * 100).toFixed(2)} \\text{ cm}`} />
                    {result.overLimit ? (
                      <>
                        <AlertDiv>
                          <BlockMath math={`\\delta_f > \\delta_{lim}`} />
                          <MdError />
                        </AlertDiv>
                        <p>Flecha acima do limite.</p>
                      </>
                    ) : (
                      <BlockMath math={`\\delta_f \\leq \\delta_{lim}`} />
                    )}
                  </MathDiv>
                </>
              ) : (
                <MathDiv>
                  <p>Houve necessidade de armadura dupla, o cálculo não prosseguiu.</p>
                </MathDiv>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Carregamentos</h3>
              <MathDiv>
                <h4>Lance</h4>
                <BlockMath math={`h_m = h + \\dfrac{e}{2}`} />
                <BlockMath math={`h_m = ${data.h} + \\dfrac{${data.e} }{2} = ${result.hm} \\text{ cm}`} />
                <BlockMath math={`g_1 = h_m \\cdot 25 = ${result.hm / 100} \\cdot 25 = ${result.g1} \\text{ kN/m}^2`} />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`p_{lance} = \\left( g_1 + g_2 + q \\right) \\cdot b  = \\left( ${result.g1} + ${data.g2} + ${data.q} \\right) \\cdot ${data.b / 100} = ${result.p1} \\text{ kN/m}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`p_{lance} = \\left( g_1 + g_2 + q \\right)`} />
                  <BlockMath
                    math={`p_{lance} = \\left( ${result.g1} + ${data.g2} + ${data.q} \\right) \\cdot ${data.b / 100}`}
                  />
                  <BlockMath math={`p_{lance} = ${result.p1} \\text{ kN/m}`} />
                </MobileOnlyDiv>
                <h4>Patamar</h4>
                <BlockMath math={`g_1 = h \\cdot 25 = ${data.h / 100} \\cdot 25 = ${result.g1p} \\text{ kN/m}^2`} />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`p_{patamar} = \\left( g_1 + g_2 + q \\right) \\cdot b = \\left( ${result.g1p} + ${data.g2} + ${data.q} \\right) \\cdot ${data.b / 100} = ${result.p2.toFixed(2)} \\text{ kN/m}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`p_{patamar} = \\left( g_1 + g_2 + q \\right) \\cdot b`} />
                  <BlockMath
                    math={`p_{patamar} = \\left( ${result.g1p} + ${data.g2} + ${data.q} \\right) \\cdot ${data.b / 100}`}
                  />
                  <BlockMath math={`p_{patamar} = ${result.p2.toFixed(2)} \\text{ kN/m}`} />
                </MobileOnlyDiv>
              </MathDiv>
              <h3>Esforços</h3>
              <MathDiv>
                <h4>Reações características de apoio</h4>
                <p>Apoio esquerdo</p>
                <BlockMath math={`R_a = ${(result.ra / (data.b / 100)).toFixed(2)} \\text{ kN/m}`} />
                <p>Apoio direito</p>
                <BlockMath math={`R_b = ${(result.rb / (data.b / 100)).toFixed(2)} \\text{ kN/m}`} />
                <h4>Momento máximo no lance</h4>
                <BlockMath math={`M_{k,máx} = ${(result.m1_max * 100).toFixed(0)} \\text{ kN} \\cdot \\text{cm}`} />
                <BlockMath math={`x_{máx} = ${(result.x1_max * 100).toFixed(0)} \\text{ cm}`} />
                <BlockMath
                  math={`\\boxed{M_{d,máx} = ${(result.m1_max * 100).toFixed(0)} \\cdot 1.4 = ${result.md1.toFixed(0)}   \\text{ kN} \\cdot \\text{cm}}`}
                />
                <h4>Momento máximo no patamar</h4>
                <BlockMath math={`M_{k,máx} = ${(result.m2_max * 100).toFixed(0)} \\text{ kN} \\cdot \\text{cm}`} />
                <BlockMath math={`x_{máx} = ${(result.x2_max * 100).toFixed(0)} \\text{ cm}`} />
                <BlockMath
                  math={`\\boxed{M_{d,máx} = ${(result.m2_max * 100).toFixed(0)} \\cdot 1.4 = ${result.md2.toFixed(0)}   \\text{ kN} \\cdot \\text{cm}}`}
                />
              </MathDiv>
              {result.bending1.xdratio != 0.45 && result.bending2.xdratio != 0.45 ? (
                <>
                  <h3>Cálculo da armação</h3>
                  <MathDiv>
                    <h4>Armação principal no lance</h4>
                    <BlockMath math={`A_{s,1}= ${result.bending1.as} \\, cm^2`} />
                    <h4>Armação principal no patamar</h4>
                    <BlockMath math={`A_{s,2}= ${result.bending2.as} \\, cm^2`} />
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="rebarSelect"
                        value={selectedRebar}
                        onChange={(event) => setSelectedRebar(event.target.value)}
                      >
                        {(Number(result.bending1.as) > Number(result.bending2.as)
                          ? result.bending1.asRebars
                          : result.bending2.asRebars
                        ).map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Serão necessários{' '}
                      {
                        (Number(result.bending1.as) > Number(result.bending2.as)
                          ? result.bending1.asRebars
                          : result.bending2.asRebars
                        ).find((r) => r.name == selectedRebar).quantity
                      }{' '}
                      barras de {selectedRebar}
                    </p>
                    <h4>Armação de distribuição</h4>
                    <BlockMath
                      math={`A_{s,1} = \\dfrac{A_s}{5} = \\dfrac{${result.as}}{5} = ${result.secondaryAs.as1.toFixed(2)} \\text{ cm}^2\\text{/m}`}
                    />
                    <BlockMath math={`A_{s,2}= ${result.secondaryAs.as2.toFixed(2)} \\text{ cm}^2\\text{/m}`} />
                    <BlockMath
                      math={`A_{s,3}= 0.5 \\cdot \\rho_{min} \\cdot 100 \\cdot h = ${result.secondaryAs.as3.toFixed(2)} \\text{ cm}^2\\text{/m}`}
                    />
                    <BlockMath
                      math={`\\boxed{A_{s,distribuicao}= ${result.secondaryAs.value.toFixed(2)} \\text{ cm}^2\\text{/m}}`}
                    />
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="secondaryRebarSelect"
                        value={selectedSecondaryRebar}
                        onChange={(event) => setSelectedSecondaryRebar(event.target.value)}
                      >
                        {result.secondaryAs.options.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário {selectedSecondaryRebar} c/
                      {result.secondaryAs.options.find((r) => r.name == selectedSecondaryRebar)?.spacing}
                    </p>
                    <ImageDiv>
                      <RebarImage />
                      <CopyImageButton
                        type="button"
                        title="Clique para copiar essa imagem"
                        onClick={() => handleCopySVG(rebarsImageRef)}
                      >
                        <FaRegCopy />
                        Copiar
                      </CopyImageButton>
                    </ImageDiv>
                  </MathDiv>
                  <h3>Inércias consideradas</h3>
                  <MathDiv>
                    <h4>Carregamento utilizado</h4>
                    <BlockMath math={`p_{lance}= \\left( g_1 + g_2 + 0.3 \\cdot q \\right) \\cdot b`} />
                    <BlockMath
                      math={`p_{lance}= \\left ( ${result.g1} + ${data.g2} + 0.3 \\cdot ${data.q} \\right) \\cdot ${data.b / 100}`}
                    />
                    <BlockMath math={`p_{lance}= ${result.p1Els.toFixed(2)} \\text{ kN/m}`} />
                    <BlockMath math={`p_{patamar}= \\left( g_1 + g_2 + 0.3 \\cdot q \\right) \\cdot b`} />
                    <BlockMath
                      math={`p_{patamar}= \\left ( ${result.g1p} + ${data.g2} + 0.3 \\cdot ${data.q} \\right) \\cdot ${data.b / 100}`}
                    />
                    <BlockMath math={`p_{patamar}= ${result.p2Els.toFixed(2)} \\text{ kN/m}`} />
                    <h4>Área de aço considerada</h4>
                    <BlockMath math={`\\boxed{A_s= ${result.as.toFixed(2)} \\text{ cm}^2}`} />
                    <h4>Inércia homogeneizada para o lance</h4>
                    <BlockMath math={`I_h= ${result.i1.ih.toFixed(2)} \\text { cm}^4`} />
                    <h4>Inércia equivalente para o lance</h4>
                    <BlockMath math={`I_{eq}= ${result.i1.ieq.toFixed(2)} \\text { cm}^4`} />
                    <h4>Inércia homogeneizada para o patamar</h4>
                    <BlockMath math={`I_h= ${result.i2.ih.toFixed(2)} \\text { cm}^4`} />
                    <h4>Inércia equivalente para o patamar</h4>
                    <BlockMath math={`I_{eq}= ${result.i2.ieq.toFixed(2)} \\text { cm}^4`} />
                  </MathDiv>
                  <h3>Verificação da deformação</h3>
                  <MathDiv>
                    <h4>Flecha imediata</h4>
                    <BlockMath math={`\\boxed{\\delta_{t,0}= ${(result.delta * 100).toFixed(2)} \\, cm}`} />
                    <h4>Flecha diferida</h4>
                    <BlockMath math={`\\delta_{t,\\infty} = \\delta_{t,0} \\cdot (1 + \\alpha_f)`} />
                    <BlockMath math={`\\alpha_f = \\Delta \\xi`} />
                    <BlockMath
                      math={`\\alpha_f = \\xi(t) - \\xi(t_0)= \\xi(70) - \\xi(${(data.t0 / 30).toFixed(2)})`}
                    />
                    <BlockMath math={`\\alpha_f = 2 - ${result.xit0.toFixed(2)} = ${result.alphaf.toFixed(2)} `} />
                    <BlockMath
                      math={`\\boxed{\\delta_{t,\\infty} = \\delta_{t,0} \\cdot ${(1 + result.alphaf).toFixed(2)} = ${(result.deltaf * 100).toFixed(2)} \\text{ cm}}`}
                    />
                    <h4>Flecha limite</h4>
                    <BlockMath math={`\\delta_{lim} = ${(result.dlim * 100).toFixed(2)} \\text{ cm}`} />
                    {result.overLimit ? (
                      <>
                        <AlertDiv>
                          <BlockMath math={`\\delta_f > \\delta_{lim}`} />
                          <MdError />
                        </AlertDiv>
                        <p>Flecha acima do limite.</p>
                      </>
                    ) : (
                      <BlockMath math={`\\delta_f \\leq \\delta_{lim}`} />
                    )}
                  </MathDiv>
                </>
              ) : (
                <MathDiv>
                  <p>Houve necessidade de armadura dupla, o cálculo não prosseguiu.</p>
                </MathDiv>
              )}
            </Result>
          </>
        )}
      </Container>
      <Tooltip id="stair-tooltip" place="top" className="app-tooltip" classNameArrow="app-tooltip-arrow" />
    </PageContainer>
  )
}
